import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { GlareEffect } from '@sorare/core/src/atoms/animations/GlareEffect';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { ANY_SPORT_MARKET_COMMON_PACKS_GROUP } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import type { unitMapping } from '@sorare/core/src/lib/style';

import { BuyPackButton } from 'components/commonCard/BuyPackButton';

import type { PackWithBuyButton_cardPack } from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({
  center: true,
})`
  will-change: transform;
  transition: transform 0.3s ease-in-out;
  &.scaleOnHover:hover,
  &.scaleOnHover:focus {
    transform: scale(1.02);
  }
`;
const PackImg = styled(GlareEffect)`
  width: calc(18 * var(--unit));
  object-fit: contain;
  height: 100%;
`;

type Props = {
  cardPack: PackWithBuyButton_cardPack;
  cardPackGroupId: string;
  onClickBuy: (cardPackGroupId: string, slug: string) => void;
  gap?: keyof typeof unitMapping;
  isPremiumPack?: boolean;
  scaleOnHover?: boolean;
};
export const PackWithBuyButton = ({
  cardPack,
  cardPackGroupId,
  onClickBuy,
  gap = 1,
  isPremiumPack,
  scaleOnHover = true,
}: Props) => {
  const { generateSportContextPath } = useSportContext();

  return (
    <Root
      center
      gap={gap}
      className={classNames({
        scaleOnHover,
      })}
    >
      <Link
        to={`${generateSportContextPath(ANY_SPORT_MARKET_COMMON_PACKS_GROUP, {
          params: {
            group: idFromObject(cardPackGroupId),
          },
        })}?pack=${cardPack.slug}`}
      >
        <PackImg
          pictureUrl={proxyUrl(cardPack.pictureUrl, {
            cropWidth: 160,
          })}
        />
      </Link>
      <BuyPackButton
        cardPack={cardPack}
        onClick={() => onClickBuy(cardPackGroupId, cardPack.slug)}
        size="small"
        color={isPremiumPack ? 'primary' : 'secondary'}
      />
    </Root>
  );
};

PackWithBuyButton.fragments = {
  cardPack: gql`
    fragment PackWithBuyButton_cardPack on CardPack {
      slug
      pictureUrl
      ...BuyPackButton_cardPack
    }
    ${BuyPackButton.fragments.cardPack}
  ` as TypedDocumentNode<PackWithBuyButton_cardPack>,
};
